import { authApi } from "#/api/authApi";
import PasswordStrengthInput from "#/components/inputs/PasswordStrengthInput";
import {
  Button,
  Divider,
  Modal,
  PasswordInput,
  PinInput,
  Text,
  TextInput,
  type ModalProps,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconConfetti } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const ForgotPasswordModal = ({ onClose, ...rest }: ModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<string>();

  const [step, setStep] = useState<number>(1); // 1 - email , 2- verify and set password, 3-sucess

  const form = useForm({
    initialValues: {
      email: "",
      otp: "",
      requirementsMet: false,
      newPassword: "",
      repeatPassword: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (step === 1) {
        if (!/^\S+@\S+$/.test(values.email)) {
          errors.email = t("auth.forgotPassword.invalidEmail");
        }
      }

      if (step === 2) {
        if (!values.otp) {
          errors.otp = t("auth.forgotPassword.enterOtp");
        }
        if (!values.requirementsMet) {
          errors.requirementsMet = t("auth.passwordValidation.doesNotMeetRequirements");
        }
        if (values.newPassword.length < 8) {
          errors.newPassword = t("auth.passwordValidation.minLength");
        }
        if (values.repeatPassword !== values.newPassword) {
          errors.repeatPassword = t("auth.passwordValidation.passwordsDoNotMatch");
        }
      }

      return errors;
    },
  });

  const sendOtp = useMutation({
    mutationFn: async () => {
      return await authApi.sendForgotPasswordOtp(form.values.email);
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      setStep(2);
    },
    onError: (error) => {
      setErrors(error.message ?? t("auth.forgotPassword.somethingWentWrong"));
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const resetPassword = useMutation({
    mutationFn: async () => {
      return await authApi.resetPssword({
        email: form.values.email,
        newPassword: form.values.repeatPassword,
        otp: form.values.otp,
      });
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      setStep(3);
    },
    onError: (error) => {
      setErrors(error.message ?? t("auth.forgotPassword.somethingWentWrong"));
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handleAction = () => {
    const result = form.validate();
    if (result.hasErrors) {
      const errorMessages = Object.values(result.errors)
        .filter((msg): msg is string => typeof msg === "string")
        .join(", ");

      setErrors(errorMessages);
      return;
    }
    if (step === 1) {
      sendOtp.mutate();
    } else if (step === 2) {
      resetPassword.mutate();
    } else {
      onClose();
      setErrors(undefined);
      form.reset();
      form.clearErrors();
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        setErrors(undefined);
        form.reset();
        form.clearErrors();
      }}
      closeOnEscape={false}
      title={t("auth.forgotPassword.title")}
      centered
      {...rest}
    >
      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          {step <= 2 && (
            <TextInput
              label={t("auth.login.email")}
              placeholder={t("auth.login.emailPlaceholder")}
              description={t("auth.forgotPassword.emailDescription")}
              type="email"
              required
              classNames={{
                error: "hidden",
              }}
              key={form.key("email")}
              {...form.getInputProps("email")}
              disabled={loading || step != 1}
            />
          )}

          {step === 2 && (
            <>
              <Divider my={20} />
              <span className="text-xs font-semibold text-black/50 mb-4">
                {t("auth.forgotPassword.otpInstruction")}
              </span>
              <div>
                <label className="text-xs font-bold" htmlFor="otp">
                  {t("auth.forgotPassword.otp")}
                </label>
                <PinInput
                  id="otp"
                  placeholder="-"
                  size="xl"
                  length={6}
                  {...form.getInputProps("otp")}
                />
              </div>

              <PasswordStrengthInput
                label={t("auth.forgotPassword.newPassword")}
                placeholder={t("auth.forgotPassword.newPasswordPlaceholder")}
                onPasswordChange={(data) => {
                  form.clearErrors();
                  setErrors(undefined);
                  form.setValues({
                    ...form.values,
                    newPassword: data.value,
                    requirementsMet: data.requirementsMet,
                  });
                }}
              />
              <PasswordInput
                placeholder={t("auth.forgotPassword.repeatPasswordPlaceholder")}
                withAsterisk
                required
                label={t("auth.forgotPassword.repeatPassword")}
                onChange={(val) => {
                  setErrors(undefined);
                  form.clearErrors();
                  form.setValues({
                    ...form.values,
                    repeatPassword: val.target.value,
                  });
                }}
              />
            </>
          )}

          {step === 3 && (
            <div className="flex flex-col gap-6 items-center">
              <div className="w-fit bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 inline-block p-5 rounded-full">
                <IconConfetti size={60} stroke={1} color="white" />
              </div>
              <span>{t("auth.forgotPassword.passwordResetSuccess")}</span>
            </div>
          )}

          {errors && (
            <Text color="red.5" size="xs">
              {errors}
            </Text>
          )}
        </div>
        <Button
          color="black"
          size="sm"
          type="submit"
          loading={loading}
          onClick={handleAction}
        >
          {step === 1 ? t("auth.forgotPassword.sendOtp") : step === 2 ? t("auth.forgotPassword.resetPassword") : t("common.close")}
        </Button>
      </div>
    </Modal>
  );
};

export default ForgotPasswordModal;
